import {
  GateMovementMessageType as MessageType,
  type GateMovementDirection,
} from '@/__generated__/graphql';
import { useFormatters } from '@/hooks/useFormatters';
import { auToneSurfaceSx } from '@/theme/au-compat';
import TimeElapsed from '@components/TimeElapsed';
import { Access, AuthorizationWrapper } from '@features/authorization';
import { formatPosition } from '@features/gateCommon/utils/formatPosition';
import { plural, t } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import { type SvgIconComponent } from '@mui/icons-material';
import { Box, Typography } from '@mui/material';
import { useMemo, type FC, type PropsWithChildren, type ReactNode } from 'react';
import type { MovementGroup } from './groupNotifications';
import {
  getAttributes,
  getTextFormatters,
  NotificationData,
  NotificationTone,
  readCode,
  readMessageCode,
  renderLine,
} from './utils';

interface FeedCardProps {
  /** Sentido del movimiento al que pertenece el mensaje; decide la flecha de un `MOVEMENT`. */
  direction?: GateMovementDirection | null;
  isFeatured?: boolean;
  animate?: boolean;
  onAnimationEnd?: () => void;
}

interface CardProps extends FeedCardProps {
  data: NotificationData;
}

/** El código crudo, en el azul de la capa. */
const CodeText: FC<{ code: string }> = ({ code }) => (
  <Box
    component="code"
    sx={{
      ml: '6px',
      flexShrink: 0,
      whiteSpace: 'nowrap',
      fontFamily: 'var(--au-mono)',
      fontSize: 10.5,
      fontWeight: 600,
      color: 'var(--au-primary-text)',
    }}
  >
    {code}
  </Box>
);

/**
 * Código crudo, solo para quien ve los campos técnicos (usuarios Capta). De módulo y no inline:
 * `AuthorizationWrapper` suspende, y una identidad inestable lo remontaría.
 */
const NotificationCode: FC<{ code: string }> = ({ code }) => (
  <AuthorizationWrapper access={Access.ViewGateMovementAdvancedFields} fallback={null}>
    <CodeText code={code} />
  </AuthorizationWrapper>
);

// El acento realza por **peso**, no por color: una segunda tinta encima de un relleno teñido queda
// con menos contraste que el enunciado que viene a destacar.
const Accent: FC<PropsWithChildren> = ({ children }) => (
  <span style={{ fontWeight: 700 }}>{children}</span>
);

const LINE_SX = { fontSize: 12.5, fontWeight: 600, lineHeight: 1.25, color: 'inherit' } as const;

/**
 * La fila del feed tiene alto fijo (`ROW_HEIGHT`) y la tarjeta recorta lo que sobra: en un panel
 * angosto una línea larga quedaba cortada arriba sin aviso. Con un tope de renglones se corta con
 * elipsis, al final.
 */
const clampSx = (lines: number) =>
  ({
    display: '-webkit-box',
    WebkitBoxOrient: 'vertical',
    WebkitLineClamp: lines,
    overflow: 'hidden',
  }) as const;

/**
 * Superficie común del feed: relleno teñido por tono, glifo, texto y hora, todos con la tinta del
 * tono (una sola tinta sobre el relleno). El destacado se distingue solo por ancho, 100% contra 90%.
 */
const FeedCard: FC<
  FeedCardProps & {
    tone: NotificationTone;
    Icon: SvgIconComponent;
    children: ReactNode;
    time: ReactNode;
  }
> = ({ tone, Icon, isFeatured = false, animate = false, onAnimationEnd, children, time }) => (
  <Box
    onAnimationEnd={onAnimationEnd}
    sx={{
      ...auToneSurfaceSx(tone, { radius: 'card', padding: '10px 18px' }),
      display: 'flex',
      alignItems: 'center',
      gap: '14px',
      width: isFeatured ? '100%' : '90%',
      mx: isFeatured ? 0 : 'auto',
      position: 'relative',
      zIndex: isFeatured ? 2 : 1,
      height: '100%',
      overflow: 'hidden',
      // La entrada no viene desde afuera del feed: toma presencia en el espacio que la lista ya
      // le reservó. En X le fabricaría un desborde al área de scroll, que no tiene ese eje.
      ...(animate && {
        transformOrigin: 'center',
        animation: 'notificationEnter 240ms cubic-bezier(0.2, 0.72, 0.25, 1) both',
        '@keyframes notificationEnter': {
          from: { transform: 'scale(0.985)', opacity: 0 },
          to: { transform: 'scale(1)', opacity: 1 },
        },
        '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
      }),
    }}
  >
    <Box sx={{ display: 'flex', flexShrink: 0, fontSize: 22, color: 'inherit' }}>
      <Icon fontSize="inherit" />
    </Box>
    {/* `.oc-notif-text` — columna centrada con el mínimo de dos líneas: la tarjeta de una línea
        mide lo mismo que la agrupada y el alto fijo de fila de la lista sigue valiendo. */}
    <Box
      sx={{
        flex: 1,
        minWidth: 0,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        minHeight: 'calc(12.5px * 1.25 + 1px + 11.5px * 1.35)',
      }}
    >
      {children}
    </Box>

    <Box sx={{ flexShrink: 0, minWidth: 76, textAlign: 'right', alignSelf: 'flex-start' }}>
      <Typography sx={{ fontSize: 10.5, color: 'inherit' }}>{time}</Typography>
    </Box>
  </Box>
);

/** Un mensaje del feed, en una línea: «qué pasó · dato», con el código crudo al final. */
export const NotificationCard: FC<CardProps> = ({ data, direction, ...cardProps }) => {
  const { i18n } = useLingui();
  const formatters = useFormatters();
  const { payload, type, timestamp, rawCode } = data;

  // `payload.closeCode` y `rawCode` son el mismo dato por dos caminos (mensaje y fila): gana el del
  // payload y `rawCode` lo respalda, con la misma lectura que el código visible para que un
  // `closeCode` en blanco no deje la línea sin causa.
  const payloadWithCode = useMemo(() => {
    if (rawCode == null || readCode(payload?.closeCode) != null) return payload;
    return { ...payload, closeCode: rawCode };
  }, [payload, rawCode]);

  const code = readMessageCode(data);

  const { Icon, tone } = getAttributes(type, direction);
  const { titleFormatter } = getTextFormatters(type, i18n, formatters);

  return (
    <FeedCard
      {...cardProps}
      tone={tone}
      Icon={Icon}
      time={<TimeElapsed timestamp={timestamp} short />}
    >
      {/* El código va fuera del texto que se corta: la elipsis nunca lo tapa. */}
      <Box sx={{ display: 'flex', alignItems: 'last baseline', minWidth: 0 }}>
        <Typography sx={{ ...LINE_SX, ...clampSx(2), minWidth: 0 }}>
          {titleFormatter(payloadWithCode, Accent)}
        </Typography>
        {code && <NotificationCode code={code} />}
      </Box>
    </FeedCard>
  );
};

/**
 * Los `MOVEMENT` consecutivos en una tarjeta. En vivo dice la posición actual y, debajo, cuántos
 * reportes, el recorrido y desde cuándo; congelada dice el recorrido. El código crudo es el del
 * reporte más reciente.
 */
export const MovementGroupCard: FC<FeedCardProps & { group: MovementGroup }> = ({
  group,
  direction,
  ...cardProps
}) => {
  const { i18n } = useLingui();
  const { formatDate, formatMetricValue } = useFormatters();
  const { Icon, tone } = getAttributes(MessageType.Movement, direction);
  const { count, from, to, firstAt, lastAt, live } = group;

  const clock = (at: number) => formatDate(at, { hour: '2-digit', minute: '2-digit' });
  const position = (value: number) => formatPosition(value, formatMetricValue);
  const range =
    from !== null && to !== null && from !== to ? `${position(from)} → ${position(to)}` : null;
  const current = to !== null ? position(to) : null;

  let lead: ReactNode;
  if (live) lead = renderLine(t(i18n)`En movimiento`, { value: current }, Accent);
  else if (range) {
    lead = (
      <>
        {t(i18n)`Se movió`} <Accent>{range}</Accent>
      </>
    );
  } else lead = renderLine(t(i18n)`Sin recorrido`, { value: current }, Accent);

  const since = clock(firstAt);
  const facts = [t(i18n)({ message: plural(count, { one: '# reporte', other: '# reportes' }) })];
  if (live) {
    if (range) facts.push(range);
    facts.push(t(i18n)`desde ${since}`);
  } else if (since !== clock(lastAt)) {
    // La hora de fin ya está a la derecha; en el mismo minuto saldría dos veces.
    facts.push(t(i18n)`desde ${since}`);
  }

  return (
    <FeedCard
      {...cardProps}
      tone={tone}
      Icon={Icon}
      time={live ? <TimeElapsed timestamp={lastAt} short /> : clock(lastAt)}
    >
      <Box sx={{ display: 'flex', alignItems: 'last baseline', minWidth: 0 }}>
        <Typography sx={{ ...LINE_SX, ...clampSx(1), minWidth: 0 }}>{lead}</Typography>
        {group.code && <NotificationCode code={group.code} />}
      </Box>
      <Typography
        sx={{ fontSize: 11.5, lineHeight: 1.35, mt: '1px', color: 'inherit', ...clampSx(1) }}
      >
        {facts.join(' · ')}
      </Typography>
    </FeedCard>
  );
};
